import type { QueryClient } from '@tanstack/react-query'
import type { TokenMeta } from '@algoboo/ui'

/*
 * Token metadata registry for display (symbols, decimals). Feed payloads
 * carry bare addresses, so symbols come from TokenRef objects seen in any
 * API response (trades, balances, positions) plus the well-known assets.
 */
const registry = new Map<string, TokenMeta>()

export function seedTokens(): void {
  registry.set('0x0000000000000000000000000000000000000000', { symbol: 'ETH', decimals: 18, name: 'Ether' })
  registry.set('0x0bd7d308f8e1639fab988df18a8011f41eacad73', { symbol: 'WETH', decimals: 18, name: 'Wrapped Ether' })
}

export function resolveToken(address: string): TokenMeta | undefined {
  return registry.get(address.toLowerCase())
}

export function knownTokens(): { address: string; symbol: string; decimals: number }[] {
  return [...registry.entries()].map(([address, m]) => ({ address, symbol: m.symbol, decimals: m.decimals }))
}

function walk(v: unknown, depth: number): void {
  if (depth > 8 || v === null || typeof v !== 'object') return
  if (Array.isArray(v)) {
    for (const x of v) walk(x, depth + 1)
    return
  }
  const o = v as Record<string, unknown>
  if (typeof o['address'] === 'string' && typeof o['symbol'] === 'string' && typeof o['decimals'] === 'number' && !registry.has(o['address'].toLowerCase())) {
    registry.set(o['address'].toLowerCase(), { symbol: o['symbol'], decimals: o['decimals'] })
  }
  // AgentTokenCard/Detail: the agent token itself
  if (typeof o['address'] === 'string' && typeof o['symbol'] === 'string' && typeof o['totalSupply'] === 'string' && typeof o['decimals'] === 'number') {
    registry.set(o['address'].toLowerCase(), { symbol: o['symbol'], decimals: o['decimals'], ...(typeof o['name'] === 'string' ? { name: o['name'] } : {}) })
  }
  for (const k in o) walk(o[k], depth + 1)
}

/** Learn token metadata from every successful query. */
export function watchTokens(qc: QueryClient): () => void {
  return qc.getQueryCache().subscribe((e) => {
    if (e.type === 'updated' && e.action.type === 'success') walk(e.query.state.data, 0)
  })
}
